import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Store, Save, X, Upload, MapPin, Phone, Mail, Globe, Clock } from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage } from '../services/imageUploadService';
import MapCoordinatePicker from '../components/MapCoordinatePicker';

interface LocalActivity {
  id?: string;
  name: string;
  category: 'ristoro' | 'accoglienza' | 'gusto' | 'utile';
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  openingHours?: string;
  specialties?: string[];
  image?: string;
  socialMedia?: any;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

const LocalActivitiesNew = () => {
  const [activities, setActivities] = useState<LocalActivity[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<LocalActivity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState<Omit<LocalActivity, 'id'>>({
    name: '',
    description: '',
    category: 'ristoro',
    address: '',
    phone: '',
    email: '',
    website: '',
    image: '',
    coordinates: {
      latitude: 40.1914,
      longitude: 15.1139
    },
    specialties: [],
    openingHours: '',
    socialMedia: {}
  });

  const categories = [
    { value: 'ristoro', label: 'Ristoro' },
    { value: 'accoglienza', label: 'Accoglienza' },
    { value: 'utili', label: 'Punti Utili' },
    { value: 'gusto', label: 'Gusto' }
  ];

  const loadActivities = async () => {
    try {
      console.log('🔄 Caricando attività locali...');
      setIsLoading(true);
      
      const activitiesRef = collection(db, 'localActivities');
      const snapshot = await getDocs(activitiesRef);
      
      if (snapshot.empty) {
        console.log('⚠️ Nessuna attività trovata in Firestore');
        setActivities([]);
        return;
      }

      const activitiesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LocalActivity[];

      console.log(`✅ Caricate ${activitiesData.length} attività locali`);
      setActivities(activitiesData);
    } catch (error) {
      console.error('❌ Errore caricamento attività:', error);
      setActivities([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadActivities();
  }, []);

  const handleNew = () => {
    setFormData({
      name: '',
      description: '',
      category: 'ristoro',
      address: '',
      phone: '',
      email: '',
      website: '',
      image: '',
      coordinates: {
        latitude: 40.1914,
        longitude: 15.1139
      },
      specialties: [],
      openingHours: '',
      socialMedia: {}
    });
    setSelectedActivity(null);
    setIsEditing(true);
  };

  const handleEdit = (activity: LocalActivity) => {
    setFormData({
      name: activity.name,
      description: activity.description,
      category: activity.category,
      address: activity.address,
      phone: activity.phone,
      email: activity.email || '',
      website: activity.website || '',
      image: activity.image,
      coordinates: activity.coordinates,
      specialties: activity.specialties || [],
      openingHours: activity.openingHours || '',
      socialMedia: activity.socialMedia || {}
    });
    setSelectedActivity(activity);
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        alert('Il nome è obbligatorio');
        return;
      }

      const activityData = {
        ...formData,
        specialties: formData.specialties?.filter((s: string) => s.trim()) || [],
        socialMedia: Object.fromEntries(
          Object.entries(formData.socialMedia || {}).filter(([_, value]: [string, any]) => value?.trim?.())
        )
      };

      if (selectedActivity) {
        await updateDoc(doc(db, 'localActivities', selectedActivity.id!), activityData);
        console.log('✅ Attività aggiornata');
      } else {
        await addDoc(collection(db, 'localActivities'), activityData);
        console.log('✅ Attività creata');
      }

      await loadActivities();
      handleCancel();
    } catch (error) {
      console.error('❌ Errore salvataggio:', error);
      alert('Errore durante il salvataggio');
    }
  };

  const handleDelete = async (activity: LocalActivity) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${activity.name}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'localActivities', activity.id!));
      console.log('✅ Attività eliminata');
      await loadActivities();
    } catch (error) {
      console.error('❌ Errore eliminazione:', error);
      alert('Errore durante l\'eliminazione');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedActivity(null);
    setFormData({
      name: '',
      description: '',
      category: 'ristoro',
      address: '',
      phone: '',
      email: '',
      website: '',
      image: '',
      coordinates: {
        latitude: 40.1914,
        longitude: 15.1139
      },
      specialties: [],
      openingHours: '',
      socialMedia: {}
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const imagePath = `localActivities/${Date.now()}-${file.name}`;
      const imageUrl = await uploadImage(file, imagePath);
      setFormData(prev => ({ ...prev, image: imageUrl }));
      console.log('✅ Immagine caricata:', imageUrl);
    } catch (error) {
      console.error('❌ Errore upload immagine:', error);
      alert('Errore durante il caricamento dell\'immagine');
    } finally {
      setIsUploading(false);
    }
  };


  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'ristoro': return '🍽️';
      case 'accoglienza': return '🏨';
      case 'utili': return '🏥';
      case 'gusto': return '🛒';
      default: return '📍';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'ristoro': return 'bg-orange-100 text-orange-800';
      case 'accoglienza': return 'bg-blue-100 text-blue-800';
      case 'utili': return 'bg-green-100 text-green-800';
      case 'gusto': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento attività locali...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white p-8">
        <div className="container-max">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Store size={24} />
              <div>
                <h1 className="text-2xl font-bold">Attività Locali</h1>
                <p className="text-white/80 text-sm mt-1">
                  Gestisci ristoranti, accoglienza, punti utili e produttori di Casal Velino
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleNew}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Nuova Attività</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-max py-8">
        {activities.length === 0 ? (
          <div className="text-center py-12">
            <Store size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna attività locale</h3>
            <p className="text-gray-600 mb-6">Inizia aggiungendo la prima attività locale.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activities.map((activity) => (
              <div key={activity.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{getCategoryIcon(activity.category)}</span>
                      <div>
                        <h3 className="font-semibold text-gray-800">{activity.name}</h3>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(activity.category)}`}>
                          {categories.find(c => c.value === activity.category)?.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEdit(activity)}
                        className="p-2 text-gray-400 hover:text-primary transition-colors"
                        title="Modifica"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(activity)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {activity.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{activity.description}</p>
                  )}

                  <div className="space-y-2 text-sm text-gray-600">
                    {activity.address && (
                      <div className="flex items-center space-x-2">
                        <MapPin size={14} />
                        <span className="truncate">{activity.address}</span>
                      </div>
                    )}
                    {activity.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone size={14} />
                        <span>{activity.phone}</span>
                      </div>
                    )}
                    {activity.email && (
                      <div className="flex items-center space-x-2">
                        <Mail size={14} />
                        <span className="truncate">{activity.email}</span>
                      </div>
                    )}
                    {activity.website && (
                      <div className="flex items-center space-x-2">
                        <Globe size={14} />
                        <span className="truncate">{activity.website}</span>
                      </div>
                    )}
                    {activity.openingHours && (
                      <div className="flex items-center space-x-2">
                        <Clock size={14} />
                        <span>{activity.openingHours}</span>
                      </div>
                    )}
                  </div>

                  {activity.specialties && activity.specialties.length > 0 && (
                    <div className="mt-4">
                      <div className="flex flex-wrap gap-1">
                        {activity.specialties.slice(0, 3).map((specialty, index) => (
                          <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                            {specialty}
                          </span>
                        ))}
                        {activity.specialties.length > 3 && (
                          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                            +{activity.specialties.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal di modifica */}
      {isEditing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">
                  {selectedActivity ? 'Modifica Attività' : 'Nuova Attività'}
                </h2>
                <button
                  onClick={handleCancel}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Nome dell'attività"
                  />
                </div>

                {/* Categoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categoria *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Descrizione */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrizione
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Descrizione dell'attività"
                  />
                </div>

                {/* Indirizzo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Indirizzo *
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Indirizzo completo"
                  />
                </div>

                {/* Telefono */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefono *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Numero di telefono"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Email (opzionale)"
                  />
                </div>

                {/* Website */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Sito web (opzionale)"
                  />
                </div>

                {/* Orari */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Orari di Apertura
                  </label>
                  <input
                    type="text"
                    value={formData.openingHours}
                    onChange={(e) => setFormData(prev => ({ ...prev, openingHours: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="es. Lun-Ven 9:00-18:00"
                  />
                </div>

                {/* Specialità */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specialità (una per riga)
                  </label>
                  <textarea
                    value={formData.specialties?.join('\n') || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      specialties: e.target.value.split('\n').filter(s => s.trim()) 
                    }))}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Una specialità per riga"
                  />
                </div>

                {/* Coordinate con Google Maps */}
                <MapCoordinatePicker
                  latitude={formData.coordinates?.latitude || 0}
                  longitude={formData.coordinates?.longitude || 0}
                  onCoordinatesChange={(lat, lng) => setFormData(prev => ({
                    ...prev,
                    coordinates: { latitude: lat, longitude: lng }
                  }))}
                  label="Coordinate Attività"
                />

                {/* Upload Immagine */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Immagine
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-lg cursor-pointer hover:bg-primary/20 transition-colors">
                      <Upload size={16} />
                      <span>{isUploading ? 'Caricamento...' : 'Scegli File'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={isUploading}
                      />
                    </label>
                    {formData.image && (
                      <span className="text-sm text-gray-600 truncate max-w-xs">
                        {formData.image.split('/').pop()}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-8">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2"
                >
                  <Save size={16} />
                  <span>Salva</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalActivitiesNew;
